import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Plus,
  Pill,
  Calendar,
  Clock,
  User,
  AlertTriangle,
  History,
  Star,
  Pause,
  Play,
  Square,
  Edit,
  FileText,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { motion } from 'framer-motion'

import { useAuth } from '@/context/AuthContext'
import {
  getMedications,
  getActiveMedications,
  getMedicationPrescriberSuggestions,
  createMedication,
  updateMedication,
  stopMedication,
  pauseMedication,
  resumeMedication,
  getMedicationHistory,
  getMedicationStatusColor,
  getMedicationStatusLabel,
  calculateMedicationDuration,
  validateMedicationInput,
} from '@/lib/medications'
import type { MedicationInput, ActiveMedication } from '@/types/health'

interface MedicationFormData {
  name: string
  dosage: string
  frequency: string
  instructions: string
  prescriber_name: string
  prescription_date: string
  start_date: string
  planned_end_date: string
  actual_end_date: string
  effectiveness_rating: number | null
  side_effects: string[]
  notes: string
}

const initialFormData: MedicationFormData = {
  name: '',
  dosage: '',
  frequency: '',
  instructions: '',
  prescriber_name: '',
  prescription_date: '',
  start_date: new Date().toISOString().split('T')[0],
  planned_end_date: '',
  actual_end_date: '',
  effectiveness_rating: null,
  side_effects: [],
  notes: '',
}

const commonFrequencies = [
  '1 fois par jour',
  '2 fois par jour',
  '3 fois par jour',
  'Matin',
  'Matin et soir',
  'Matin, midi et soir',
  'Avant les repas',
  'Après les repas',
  'Au coucher',
  'Si besoin',
  'Autre'
]

const commonSideEffects = [
  'Nausées',
  'Fatigue',
  'Maux de tête',
  'Troubles digestifs',
  'Vertiges',
  'Somnolence',
  'Perte d\'appétit',
  'Sécheresse buccale',
  'Constipation',
  'Diarrhée'
]

function normalizeSuggestionSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function MedicationCard({
  medication,
  onEdit,
  onStop,
  onPause,
  onResume,
  onViewHistory
}: {
  medication: ActiveMedication
  onEdit: (medication: ActiveMedication) => void
  onStop: (medication: ActiveMedication) => void
  onPause: (medication: ActiveMedication) => void
  onResume: (medication: ActiveMedication) => void
  onViewHistory: (medication: ActiveMedication) => void
}) {
  const duration = calculateMedicationDuration(
    medication.start_date,
    medication.actual_end_date
  )
  const statusColor = getMedicationStatusColor(medication.current_status)
  const statusLabel = getMedicationStatusLabel(medication.current_status)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card hover:border-primary-800/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-white">{medication.name}</h3>
              <p className="text-sm text-slate-400 mt-1">
                {medication.dosage} {medication.frequency && `• ${medication.frequency}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`badge border ${statusColor}`}>
                {statusLabel}
              </span>
              {medication.effectiveness_rating && (
                <div className="flex items-center gap-1 text-amber-400">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm">{medication.effectiveness_rating}/10</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              {medication.prescriber_name && (
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <User className="w-4 h-4" />
                  <span>Dr. {medication.prescriber_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Calendar className="w-4 h-4" />
                <span>Débuté le {format(new Date(medication.start_date), 'd MMMM yyyy', { locale: fr })}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Clock className="w-4 h-4" />
                <span>Durée: {duration.label}</span>
              </div>
            </div>

            <div className="space-y-2">
              {medication.planned_end_date && (
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Calendar className="w-4 h-4" />
                  <span>Fin prévue: {format(new Date(medication.planned_end_date), 'd MMM yyyy', { locale: fr })}</span>
                </div>
              )}
              {medication.actual_end_date && (
                <div className="flex items-center gap-2 text-sm text-rose-200">
                  <Calendar className="w-4 h-4" />
                  <span>Arrete le {format(new Date(medication.actual_end_date), 'd MMM yyyy', { locale: fr })}</span>
                </div>
              )}
              {medication.side_effects?.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-orange-300">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{medication.side_effects.length} effet{medication.side_effects.length > 1 ? 's' : ''} secondaire{medication.side_effects.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>

          {medication.instructions && (
            <div className="mt-4 p-3 rounded-lg bg-dark-surface-high border border-dark-border">
              <p className="text-sm text-slate-300">{medication.instructions}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-dark-border">
        <button
          onClick={() => onViewHistory(medication)}
          className="btn-ghost text-sm"
        >
          <History className="w-4 h-4" />
          Historique
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(medication)}
            className="btn-ghost text-sm"
          >
            <Edit className="w-4 h-4" />
            Modifier
          </button>

          {medication.current_status === 'active' && (
            <>
              <button
                onClick={() => onPause(medication)}
                className="btn-ghost text-sm text-yellow-400 hover:text-yellow-300"
              >
                <Pause className="w-4 h-4" />
                Pause
              </button>
              <button
                onClick={() => onStop(medication)}
                className="btn-ghost text-sm text-red-400 hover:text-red-300"
              >
                <Square className="w-4 h-4" />
                Arrêter
              </button>
            </>
          )}

          {medication.current_status === 'paused' && (
            <button
              onClick={() => onResume(medication)}
              className="btn-ghost text-sm text-green-400 hover:text-green-300"
            >
              <Play className="w-4 h-4" />
              Reprendre
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}

function MedicationForm({
  medication,
  prescriberSuggestions,
  onSubmit,
  onCancel
}: {
  medication?: ActiveMedication | null
  prescriberSuggestions: string[]
  onSubmit: (data: MedicationFormData) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState<MedicationFormData>(
    medication ? {
      name: medication.name,
      dosage: medication.dosage || '',
      frequency: medication.frequency || '',
      instructions: medication.instructions || '',
      prescriber_name: medication.prescriber_name || '',
      prescription_date: medication.prescription_date || '',
      start_date: medication.start_date,
      planned_end_date: medication.planned_end_date || '',
      actual_end_date: medication.actual_end_date || '',
      effectiveness_rating: medication.effectiveness_rating,
      side_effects: medication.side_effects || [],
      notes: medication.notes || '',
    } : initialFormData
  )

  const [errors, setErrors] = useState<string[]>([])
  const [sideEffectInput, setSideEffectInput] = useState('')
  const visiblePrescriberSuggestions = prescriberSuggestions
    .filter((suggestion) => {
      const query = normalizeSuggestionSearch(formData.prescriber_name)

      if (!query) {
        return true
      }

      return normalizeSuggestionSearch(suggestion).includes(query)
    })
    .slice(0, 8)

  useEffect(() => {
    setFormData(
      medication ? {
        name: medication.name,
        dosage: medication.dosage || '',
        frequency: medication.frequency || '',
        instructions: medication.instructions || '',
        prescriber_name: medication.prescriber_name || '',
        prescription_date: medication.prescription_date || '',
        start_date: medication.start_date,
        planned_end_date: medication.planned_end_date || '',
        actual_end_date: medication.actual_end_date || '',
        effectiveness_rating: medication.effectiveness_rating,
        side_effects: medication.side_effects || [],
        notes: medication.notes || '',
      } : initialFormData
    )
    setErrors([])
    setSideEffectInput('')
  }, [medication])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const validation = validateMedicationInput({
      patient_id: '', // Sera ajouté dans le composant parent
      ...formData
    })

    if (!validation.isValid) {
      setErrors(validation.errors)
      return
    }

    setErrors([])
    onSubmit(formData)
  }

  const addSideEffect = (effect: string) => {
    if (effect && !formData.side_effects.includes(effect)) {
      setFormData(prev => ({
        ...prev,
        side_effects: [...prev.side_effects, effect]
      }))
    }
    setSideEffectInput('')
  }

  const removeSideEffect = (effect: string) => {
    setFormData(prev => ({
      ...prev,
      side_effects: prev.side_effects.filter(e => e !== effect)
    }))
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-dark-surface border border-dark-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-white mb-6">
          {medication ? 'Modifier le médicament' : 'Nouveau médicament'}
        </h2>

        {errors.length > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/20 border border-red-800/40">
            <ul className="text-sm text-red-300 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Nom du médicament *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input"
                placeholder="Ex: Mesalazine"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Dosage
              </label>
              <input
                type="text"
                value={formData.dosage}
                onChange={(e) => setFormData(prev => ({ ...prev, dosage: e.target.value }))}
                className="input"
                placeholder="Ex: 500mg"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Fréquence
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
                className="input"
              >
                <option value="">Sélectionner...</option>
                {commonFrequencies.map(freq => (
                  <option key={freq} value={freq}>{freq}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Prescripteur
              </label>
              <input
                type="text"
                list="medication-prescriber-suggestions"
                value={formData.prescriber_name}
                onChange={(e) => setFormData(prev => ({ ...prev, prescriber_name: e.target.value }))}
                className="input"
                placeholder="Rechercher un medecin ou saisir un nouveau nom"
              />
              <datalist id="medication-prescriber-suggestions">
                {prescriberSuggestions.map((suggestion) => (
                  <option key={suggestion} value={suggestion} />
                ))}
              </datalist>
              {visiblePrescriberSuggestions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {visiblePrescriberSuggestions.slice(0, 6).map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, prescriber_name: suggestion }))}
                      className="rounded-full border border-dark-border bg-dark-surface-high px-2.5 py-1 text-xs text-slate-300 transition-colors hover:text-white"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-2 text-xs text-slate-500">
                Recherche rapide parmi les medecins connus, avec saisie libre si besoin.
              </p>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Date de prescription
              </label>
              <input
                type="date"
                value={formData.prescription_date}
                onChange={(e) => setFormData(prev => ({ ...prev, prescription_date: e.target.value }))}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Date de début *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Date de fin prévue
              </label>
              <input
                type="date"
                value={formData.planned_end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, planned_end_date: e.target.value }))}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Date de fin réelle
              </label>
              <input
                type="date"
                value={formData.actual_end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, actual_end_date: e.target.value }))}
                className="input"
              />
              <p className="mt-2 text-xs text-slate-500">
                À renseigner si le traitement est arrêté ou terminé, et modifiable plus tard si besoin.
              </p>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">
                Efficacité (1-10)
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.effectiveness_rating || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  effectiveness_rating: e.target.value ? Number(e.target.value) : null
                }))}
                className="input"
                placeholder="1-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">
              Instructions
            </label>
            <textarea
              value={formData.instructions}
              onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
              className="textarea"
              placeholder="Instructions spéciales, précautions..."
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">
              Effets secondaires
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={sideEffectInput}
                onChange={(e) => setSideEffectInput(e.target.value)}
                className="input flex-1"
                placeholder="Ajouter un effet secondaire..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addSideEffect(sideEffectInput)
                  }
                }}
              />
              <button
                type="button"
                onClick={() => addSideEffect(sideEffectInput)}
                className="btn-secondary"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              {commonSideEffects.map(effect => (
                <button
                  key={effect}
                  type="button"
                  onClick={() => addSideEffect(effect)}
                  className="text-xs px-2 py-1 rounded-full bg-dark-surface-high text-slate-300 hover:text-white transition-colors"
                >
                  + {effect}
                </button>
              ))}
            </div>

            {formData.side_effects.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.side_effects.map(effect => (
                  <span
                    key={effect}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-900/20 text-orange-300 text-xs border border-orange-800/40"
                  >
                    {effect}
                    <button
                      type="button"
                      onClick={() => removeSideEffect(effect)}
                      className="hover:text-orange-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="textarea"
              placeholder="Notes personnelles sur ce médicament..."
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="btn-ghost"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {medication ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}

export default function Treatments() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingMedication, setEditingMedication] = useState<ActiveMedication | null>(null)
  const [viewingHistory, setViewingHistory] = useState<ActiveMedication | null>(null)
  const [activeTab, setActiveTab] = useState<'active' | 'all' | 'history'>('active')

  // Queries
  const { data: activeMedications = [], isLoading: loadingActive } = useQuery({
    queryKey: ['medications', user?.id, 'active'],
    queryFn: () => getActiveMedications(user!.id),
    enabled: Boolean(user),
  })

  const { data: allMedications = [], isLoading: loadingAll } = useQuery({
    queryKey: ['medications', user?.id, 'all'],
    queryFn: () => getMedications(user!.id),
    enabled: Boolean(user) && activeTab === 'all',
  })

  const { data: prescriberSuggestions = [] } = useQuery({
    queryKey: ['medication-prescribers', user?.id],
    queryFn: () => getMedicationPrescriberSuggestions(user!.id),
    enabled: Boolean(user),
  })

  const { data: medicationHistory = [] } = useQuery({
    queryKey: ['medication-history', viewingHistory?.id],
    queryFn: () => getMedicationHistory(viewingHistory!.id),
    enabled: Boolean(viewingHistory),
  })

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (data: MedicationFormData) => {
      const input: MedicationInput = {
        patient_id: user!.id,
        ...data,
        side_effects: data.side_effects || []
      }
      return createMedication(input)
    },
    onSuccess: () => {
      toast.success('Médicament ajouté avec succès')
      queryClient.invalidateQueries({ queryKey: ['medications', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['medication-prescribers', user?.id] })
      setShowForm(false)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'ajout')
    }
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MedicationFormData }) => {
      return updateMedication(id, data)
    },
    onSuccess: () => {
      toast.success('Médicament modifié avec succès')
      queryClient.invalidateQueries({ queryKey: ['medications', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['medication-prescribers', user?.id] })
      setShowForm(false)
      setEditingMedication(null)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la modification')
    }
  })

  const stopMutation = useMutation({
    mutationFn: async ({
      medication,
      reason,
      stopDate
    }: {
      medication: ActiveMedication
      reason: string
      stopDate?: string
    }) => {
      return stopMedication(medication.id, reason, stopDate)
    },
    onSuccess: () => {
      toast.success('Médicament arrêté')
      queryClient.invalidateQueries({ queryKey: ['medications', user?.id] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'arrêt')
    }
  })

  const pauseMutation = useMutation({
    mutationFn: async ({ medication, reason }: { medication: ActiveMedication; reason: string }) => {
      return pauseMedication(medication.id, reason)
    },
    onSuccess: () => {
      toast.success('Médicament mis en pause')
      queryClient.invalidateQueries({ queryKey: ['medications', user?.id] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la pause')
    }
  })

  const resumeMutation = useMutation({
    mutationFn: async (medication: ActiveMedication) => {
      return resumeMedication(medication.id)
    },
    onSuccess: () => {
      toast.success('Médicament repris')
      queryClient.invalidateQueries({ queryKey: ['medications', user?.id] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la reprise')
    }
  })

  const handleEdit = (medication: ActiveMedication) => {
    setEditingMedication(medication)
    setShowForm(true)
  }

  const handleStop = (medication: ActiveMedication) => {
    const reason = window.prompt(`Pourquoi arrêtez-vous ${medication.name} ?`)
    if (reason) {
      const today = new Date().toISOString().split('T')[0]
      const stopDate = window.prompt(
        `Date d'arrêt pour ${medication.name} (YYYY-MM-DD)`,
        medication.actual_end_date || today
      )

      if (stopDate !== null) {
        stopMutation.mutate({
          medication,
          reason,
          stopDate: stopDate.trim() || today
        })
      }
    }
  }

  const handlePause = (medication: ActiveMedication) => {
    const reason = window.prompt(`Pourquoi mettez-vous ${medication.name} en pause ?`)
    if (reason) {
      pauseMutation.mutate({ medication, reason })
    }
  }

  const handleResume = (medication: ActiveMedication) => {
    resumeMutation.mutate(medication)
  }

  const handleViewHistory = (medication: ActiveMedication) => {
    setViewingHistory(medication)
  }

  const handleSubmitForm = (data: MedicationFormData) => {
    if (editingMedication) {
      updateMutation.mutate({ id: editingMedication.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const isLoading = loadingActive || loadingAll

  const displayedMedications = activeTab === 'active' ? activeMedications : allMedications

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Traitements</h1>
          <p className="mt-1 text-slate-400">
            Les traitements en cours d abord, puis l historique si besoin.
          </p>
        </div>

        <button
          onClick={() => {
            setEditingMedication(null)
            setShowForm(true)
          }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" />
          Nouveau médicament
        </button>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-4 py-3">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Actifs</p>
            <p className="mt-2 text-2xl font-semibold text-white">
              {activeMedications.filter(m => m.current_status === 'active').length}
            </p>
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-4 py-3">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500">En pause</p>
            <p className="mt-2 text-2xl font-semibold text-white">
              {activeMedications.filter(m => m.current_status === 'paused').length}
            </p>
          </div>

          <div className="rounded-2xl border border-dark-border bg-dark-surface-high px-4 py-3">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Effets signalés</p>
            <p className="mt-2 text-2xl font-semibold text-white">
              {activeMedications.reduce((total, m) => total + (m.side_effects?.length || 0), 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="flex space-x-1 rounded-lg bg-dark-surface-high p-1">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'active'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          En cours ({activeMedications.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-md transition-colors text-sm ${
            activeTab === 'all'
              ? 'bg-primary-600 text-white'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Tous
        </button>
      </div>

      {/* Liste des médicaments */}
      {isLoading ? (
        <div className="card text-center py-8">
          <div className="spinner mx-auto" />
          <p className="text-slate-400 mt-4">Chargement des médicaments...</p>
        </div>
      ) : displayedMedications.length === 0 ? (
        <div className="card text-center py-8">
          <Pill className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            {activeTab === 'active' ? 'Aucun médicament actif' : 'Aucun médicament enregistré'}
          </h3>
          <p className="text-slate-400 mb-6">
            {activeTab === 'active'
              ? 'Ajoutez vos médicaments actuels pour commencer le suivi'
              : 'Commencez par ajouter vos premiers médicaments'
            }
          </p>
          <button
            onClick={() => {
              setEditingMedication(null)
              setShowForm(true)
            }}
            className="btn-primary"
          >
            <Plus className="w-4 h-4" />
            Ajouter un médicament
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {displayedMedications.map((medication) => (
            <MedicationCard
              key={medication.id}
              medication={medication}
              onEdit={handleEdit}
              onStop={handleStop}
              onPause={handlePause}
              onResume={handleResume}
              onViewHistory={handleViewHistory}
            />
          ))}
        </div>
      )}

      {/* Formulaire d'ajout/édition */}
      {showForm && (
        <MedicationForm
          medication={editingMedication}
          prescriberSuggestions={prescriberSuggestions}
          onSubmit={handleSubmitForm}
          onCancel={() => {
            setShowForm(false)
            setEditingMedication(null)
          }}
        />
      )}

      {/* Modal d'historique */}
      {viewingHistory && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setViewingHistory(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-dark-surface border border-dark-border rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                Historique: {viewingHistory.name}
              </h2>
              <button
                onClick={() => setViewingHistory(null)}
                className="btn-ghost"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              {medicationHistory.length === 0 ? (
                <p className="text-slate-400 text-center py-8">
                  Aucun historique disponible pour ce médicament
                </p>
              ) : (
                medicationHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-4 p-4 rounded-lg bg-dark-surface-high border border-dark-border"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-primary-300" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="font-medium text-white capitalize">
                          {entry.action === 'started' && 'Médicament débuté'}
                          {entry.action === 'modified' && 'Médicament modifié'}
                          {entry.action === 'paused' && 'Médicament mis en pause'}
                          {entry.action === 'resumed' && 'Médicament repris'}
                          {entry.action === 'stopped' && 'Médicament arrêté'}
                          {entry.action === 'completed' && 'Traitement terminé'}
                        </h4>
                        <span className="text-xs text-slate-400">
                          {format(new Date(entry.change_date), 'd MMM yyyy à HH:mm', { locale: fr })}
                        </span>
                      </div>
                      {entry.reason && (
                        <p className="text-sm text-slate-300 mt-1">
                          Raison: {entry.reason}
                        </p>
                      )}
                      {entry.notes && (
                        <p className="text-sm text-slate-400 mt-1">
                          {entry.notes}
                        </p>
                      )}
                      {(entry.previous_status !== entry.new_status) && (
                        <p className="text-xs text-slate-500 mt-2">
                          {entry.previous_status} → {entry.new_status}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  )
}
